// Keyboard focus trap for modal dialogs.
//
// The shared ``Modal`` component used to set ``role="dialog"`` /
// ``aria-modal="true"`` but never trapped Tab — pressing Tab from
// the last focusable element inside the dialog walked focus into
// the page underneath, which screen-reader and keyboard-only
// users hit immediately when the dialog tried to gather a single
// confirm decision.
//
// This hook attaches a Tab/Shift+Tab handler that wraps focus
// within the container. It also moves initial focus to the first
// focusable child when the dialog opens (so the keyboard user
// doesn't have to Tab into the dialog from wherever they were
// before), and restores focus to the trigger element on close.
//
// Usage:
//
//     const ref = useFocusTrap(open);
//     return open ? <div ref={ref} role="dialog">...</div> : null;
//
// The container ref is what the hook returns — wire it to the
// dialog's root element.

import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

function focusableChildren(root) {
  if (!root) return [];
  const list = root.querySelectorAll(FOCUSABLE_SELECTOR);
  // ``offsetParent === null`` filters out hidden / display-none
  // children that the selector still matches. We want the visible
  // tab order only.
  return Array.from(list).filter(
    (el) => !el.hasAttribute("disabled") && el.offsetParent !== null,
  );
}

export default function useFocusTrap(active) {
  const containerRef = useRef(null);
  const restoreRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    // Remember whoever was focused before the dialog opened so we
    // can put focus back there on close. Falls back to no-op when
    // the previously-focused element has unmounted.
    restoreRef.current = document.activeElement;

    const node = containerRef.current;
    // Move focus into the dialog. Prefer the first focusable
    // child; if there isn't one, focus the container itself (we
    // give it ``tabIndex={-1}`` at the call site so it can accept
    // focus programmatically without entering the tab order).
    const focusables = focusableChildren(node);
    if (focusables.length > 0) {
      focusables[0].focus();
    } else if (node) {
      node.focus();
    }

    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const targets = focusableChildren(node);
      if (targets.length === 0) {
        e.preventDefault();
        return;
      }
      const first = targets[0];
      const last = targets[targets.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      // Restore focus to the trigger. Wrapped in try/catch because
      // the previously-focused element might have unmounted (eg.
      // a row whose containing list re-rendered).
      try {
        restoreRef.current?.focus?.();
      } catch {
        /* ignore */
      }
    };
  }, [active]);

  return containerRef;
}
